import React, { useEffect, useMemo, useState } from 'react';
import { X, GitCompareArrows } from 'lucide-react';
import { useQueries, useQuery } from '@tanstack/react-query';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Customized, ReferenceArea
} from 'recharts';
import { apiService, queryKeys } from '@/services/api';
import type { DivisionPlayer, DivisionInfo } from '@/types/api';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import { ALL_GAME_TABS, getDivisionInfoById, inferAvailableGameTabs } from '@/utils/divisionGameTabs';
import {
  buildYearRangeOptions,
  getMaxAvailableYears,
  getSelectedYearRangeValue,
} from '@/utils/historyRangeOptions';
import {
  buildCompareChartData,
  buildYearBackgroundBands,
  buildYearSections,
  getComparePointMeta,
  makeCompareTooltipContent,
  getTimelineFirstPlayedDate,
  makeYearLabelRenderer,
  makeDateAxisTickRenderer,
  renderHistoryPointActiveDot,
  renderHistoryPointDot,
  type CompareChartRow,
} from '@/utils/historyChart';

function formatDivisionLabel(div: DivisionInfo, isCurrent: boolean): string {
  const fmt = (s: string) => {
    const d = new Date(s.includes('T') ? s : s + 'T12:00:00');
    return d.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
  };
  const dates = div.first_match_date && div.last_match_date
    ? ` (${fmt(div.first_match_date)}–${fmt(div.last_match_date)})`
    : '';
  const tag = isCurrent ? ' ★' : '';
  return `#${div.division_id}${dates}${tag}`;
}

interface TeamCompareModalProps {
  isOpen: boolean;
  onClose: () => void;
  teamA: { name: string; players: DivisionPlayer[] };
  teamB: { name: string; players: DivisionPlayer[] };
  activeGameTab: string;
  divisionId?: string;
}

const GAME_OPTIONS = [
  { value: '8-ball' as const, label: '8-Ball' },
  { value: '9-ball' as const, label: '9-Ball' },
  { value: '10-ball' as const, label: '10-Ball' },
];

// Team A uses warm tones, Team B uses cool tones
const TEAM_A_COLORS = ['#f59e0b', '#ea580c', '#a855f7', '#92400e', '#fb923c', '#c084fc', '#d97706', '#7c2d12'];
const TEAM_B_COLORS = ['#2563eb', '#4338ca', '#475569', '#1d4ed8', '#6366f1', '#0f172a', '#7c3aed', '#3730a3'];

export const TeamCompareModal: React.FC<TeamCompareModalProps> = ({
  isOpen,
  onClose,
  teamA,
  teamB,
  activeGameTab,
  divisionId,
}) => {
  const gameLabel = activeGameTab === '9ball' ? '9-ball' : activeGameTab === '10ball' ? '10-ball' : '8-ball';
  const [gameType, setGameType] = useState<'8-ball' | '9-ball' | '10-ball'>(gameLabel as any);
  const [days, setDays] = useState<number | undefined>(divisionId ? undefined : 90);
  const [hiddenPlayers, setHiddenPlayers] = useState<Set<string>>(new Set());
  const [selectedDivision, setSelectedDivision] = useState<string | undefined>(divisionId);
  const [focusedTooltipPlayerId, setFocusedTooltipPlayerId] = useState<string | null>(null);

  const { data: divisions } = useQuery({
    queryKey: queryKeys.divisions,
    queryFn: () => apiService.getDivisions(),
    staleTime: 5 * 60 * 1000,
    enabled: isOpen,
  });

  const selectedDivisionInfo = useMemo(
    () => getDivisionInfoById(divisions, selectedDivision),
    [divisions, selectedDivision]
  );

  const availableGameOptions = useMemo(() => {
    const tabs = inferAvailableGameTabs(selectedDivisionInfo?.name);
    return GAME_OPTIONS.filter((option) => tabs.includes(option.value.replace('-', '') as typeof ALL_GAME_TABS[number]));
  }, [selectedDivisionInfo?.name]);

  useEffect(() => {
    if (!availableGameOptions.some((option) => option.value === gameType)) {
      setGameType(availableGameOptions[0]?.value ?? '8-ball');
    }
  }, [availableGameOptions, gameType]);

  const allPlayers = useMemo(() => [
    ...teamA.players.map(p => ({ id: p.player_id, name: p.name, team: 'A' as const })),
    ...teamB.players.map(p => ({ id: p.player_id, name: p.name, team: 'B' as const })),
  ], [teamA, teamB]);

  const togglePlayer = (playerId: string) => {
    setHiddenPlayers(prev => {
      const next = new Set(prev);
      next.has(playerId) ? next.delete(playerId) : next.add(playerId);
      return next;
    });
  };

  const csrQueries = useQueries({
    queries: allPlayers.map(player => ({
      queryKey: queryKeys.csrTimeline(player.id, gameType, selectedDivision ? undefined : days, selectedDivision),
      queryFn: () => apiService.getCSRTimeline(player.id, gameType, selectedDivision ? undefined : days, selectedDivision),
      staleTime: 5 * 60 * 1000,
      enabled: isOpen,
    })),
  });

  const fullRangeQueries = useQueries({
    queries: allPlayers.map(player => ({
      queryKey: queryKeys.csrTimeline(player.id, gameType, undefined, undefined),
      queryFn: () => apiService.getCSRTimeline(player.id, gameType, undefined, undefined),
      staleTime: 5 * 60 * 1000,
      enabled: isOpen && !selectedDivision,
    })),
  });

  const isLoading = csrQueries.some(q => q.isLoading);

  // Compute team aggregate stats from the rankings data
  const teamStats = useMemo(() => {
    const gameKey = gameType.replace('-', '');
    const computeStats = (players: DivisionPlayer[]) => {
      const withData = players.filter(p => p.games[gameKey]?.skill_level);
      const avgCSR = withData.length
        ? Math.round(withData.reduce((s, p) => s + (p.games[gameKey]?.skill_level || 0), 0) / withData.length)
        : 0;
      const totalWins = withData.reduce((s, p) => s + (p.games[gameKey]?.wins || 0), 0);
      const totalLosses = withData.reduce((s, p) => s + (p.games[gameKey]?.losses || 0), 0);
      const totalMatches = withData.reduce((s, p) => s + (p.games[gameKey]?.matches_played || 0), 0);
      const winPct = totalWins + totalLosses > 0 ? Math.round((totalWins / (totalWins + totalLosses)) * 100) : 0;
      return { avgCSR, totalWins, totalLosses, totalMatches, winPct, playerCount: players.length };
    };
    return { a: computeStats(teamA.players), b: computeStats(teamB.players) };
  }, [teamA, teamB, gameType]);

  // Merge CSR data into chart
  const chartData = useMemo(() => {
    return buildCompareChartData(
      allPlayers.map((player, idx) => {
        const visibleTimeline = csrQueries[idx].data?.timeline;
        const fullTimeline = fullRangeQueries[idx]?.data?.timeline;
        return {
          playerId: player.id,
          timeline: visibleTimeline,
          earliestActualDate: getTimelineFirstPlayedDate(fullTimeline ?? visibleTimeline),
        };
      })
    );
  }, [csrQueries, fullRangeQueries, allPlayers]);

  const axisTick = useMemo(
    () => makeDateAxisTickRenderer(new Map(chartData.map((point) => [point.date, point]))),
    [chartData]
  );

  const fullDateDisplayByDate = useMemo(
    () => new Map(chartData.map((point) => [point.date, point.fullDateDisplay])),
    [chartData]
  );

  const tooltipContent = useMemo(
    () => makeCompareTooltipContent(
      fullDateDisplayByDate,
      (playerId) => allPlayers.find((player) => player.id === playerId)?.name ?? playerId,
      focusedTooltipPlayerId
    ),
    [allPlayers, focusedTooltipPlayerId, fullDateDisplayByDate]
  );

  const yearRangeOptions = useMemo(
    () => buildYearRangeOptions(getMaxAvailableYears(fullRangeQueries.map((query) => query.data?.timeline))),
    [fullRangeQueries]
  );

  const selectedYearRangeValue = useMemo(
    () => getSelectedYearRangeValue(days),
    [days]
  );

  const yearBands = useMemo(
    () => buildYearBackgroundBands(chartData.map((point) => point.date)),
    [chartData]
  );

  const yearSections = useMemo(
    () => buildYearSections(chartData.map((point) => point.date)),
    [chartData]
  );

  const yearLabelRenderer = useMemo(
    () => makeYearLabelRenderer(yearSections),
    [yearSections]
  );

  if (!isOpen) return null;

  const renderPlayerDot = (playerId: string) => (props: any) => {
    const meta = getComparePointMeta(props.payload as CompareChartRow, playerId);
    if (!meta || props.value == null) return <g />;
    return renderHistoryPointDot({
      ...props,
      payload: meta,
      onMouseEnter: () => setFocusedTooltipPlayerId(playerId),
      onMouseLeave: () => setFocusedTooltipPlayerId((current) => (current === playerId ? null : current)),
    });
  };

  const renderPlayerActiveDot = (playerId: string) => (props: any) => {
    const meta = getComparePointMeta(props.payload as CompareChartRow, playerId);
    if (!meta || props.value == null) return <g />;
    return renderHistoryPointActiveDot({
      ...props,
      payload: meta,
      onMouseEnter: () => setFocusedTooltipPlayerId(playerId),
      onMouseLeave: () => setFocusedTooltipPlayerId((current) => (current === playerId ? null : current)),
    });
  };

  const getColor = (player: typeof allPlayers[0]) => {
    const teamPlayers = allPlayers.filter(p => p.team === player.team);
    const idx = teamPlayers.indexOf(player);
    return player.team === 'A'
      ? TEAM_A_COLORS[idx % TEAM_A_COLORS.length]
      : TEAM_B_COLORS[idx % TEAM_B_COLORS.length];
  };

  const visibleStartMarkers = allPlayers
    .filter((player) => !hiddenPlayers.has(player.id))
    .map((player) => {
      const firstRow = chartData.find((row) => typeof row[player.id] === 'number');
      const value = firstRow?.[player.id];
      return typeof value === 'number'
        ? { playerId: player.id, value, color: getColor(player) }
        : null;
    })
    .filter((marker): marker is { playerId: string; value: number; color: string } => marker !== null);

  const renderStartMarkers = (props: any) => {
    const yAxis = Object.values(props?.yAxisMap ?? {})[0] as { scale?: (value: number) => number } | undefined;
    const offset = props?.offset as { left?: number } | undefined;
    const scale = yAxis?.scale;
    if (!scale || offset?.left == null) return null;

    const markerX = offset.left - 18;
    const tickEndX = offset.left - 4;

    return (
      <g>
        {visibleStartMarkers.map((marker) => (
          <g key={marker.playerId}>
            <line
              x1={markerX + 7}
              y1={scale(marker.value)}
              x2={tickEndX}
              y2={scale(marker.value)}
              stroke={marker.color}
              strokeWidth={2}
              strokeLinecap="round"
            />
            <circle
              cx={markerX}
              cy={scale(marker.value)}
              r={6}
              fill={marker.color}
              stroke="#ffffff"
              strokeWidth={2}
            />
          </g>
        ))}
      </g>
    );
  };

  return (
    <div className="fixed inset-0 z-50 modal-backdrop flex items-end md:items-center justify-center p-0 md:p-4" onClick={onClose}>
      <div
        className="bg-white w-full h-full md:h-auto md:rounded-2xl shadow-2xl md:max-w-6xl md:max-h-[90vh] overflow-hidden flex flex-col"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-4 border-b border-slate-100">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-brand-50 rounded-lg">
              <GitCompareArrows className="w-5 h-5 text-brand-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">
                <span className="text-red-600">{teamA.name}</span>
                <span className="text-slate-400 mx-2">vs</span>
                <span className="text-indigo-600">{teamB.name}</span>
              </h2>
              <p className="text-sm text-slate-400">
                Team comparison · {teamA.players.length + teamB.players.length} players
                {selectedDivision && divisions ? (() => {
                  const div = divisions.find((d: DivisionInfo) => d.division_id === selectedDivision);
                  return div ? ` · ${div.name || `Division ${div.division_id}`}` : '';
                })() : ''}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center gap-3 md:gap-4 px-4 md:px-6 py-3 border-b border-slate-100 bg-slate-50/50">
          <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
            {availableGameOptions.map(opt => (
              <button
                key={opt.value}
                onClick={() => setGameType(opt.value)}
                className={`px-3 py-1.5 text-sm font-medium rounded-md transition-all ${
                  gameType === opt.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          {!selectedDivision && (
            <div className="flex items-center gap-2">
              <div className="flex gap-1 p-0.5 bg-slate-200/60 rounded-lg">
                {[
                  { value: 30, label: '30d' },
                  { value: 90, label: '90d' },
                  { value: 180, label: '6mo' },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => setDays(opt.value)}
                    className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                      days === opt.value ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
              <select
                value={selectedYearRangeValue}
                onChange={(e) => {
                  const option = yearRangeOptions.find((entry) => entry.value === e.target.value);
                  setDays(option?.days);
                }}
                className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
              >
                <option value="" disabled>Years</option>
                {yearRangeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {divisions && divisions.length > 0 && (
            <select
              value={selectedDivision || ''}
              onChange={e => setSelectedDivision(e.target.value || undefined)}
              className="px-2.5 py-1.5 text-xs font-medium rounded-lg bg-slate-200/60 border-0 text-slate-600 focus:outline-none focus:ring-2 focus:ring-brand-300"
            >
              <option value="">All Divisions</option>
              {divisions.map((div: DivisionInfo, idx: number) => (
                <option key={div.division_id} value={div.division_id}>
                  {formatDivisionLabel(div, idx === 0)}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Team stat cards */}
        <div className="grid grid-cols-2 gap-3 md:gap-4 px-4 md:px-6 py-4 border-b border-slate-100">
          {[
            { team: teamA, stats: teamStats.a, color: 'red' },
            { team: teamB, stats: teamStats.b, color: 'indigo' },
          ].map(({ team, stats, color }) => (
            <div key={team.name} className={`rounded-xl border p-4 ${color === 'red' ? 'border-red-200 bg-red-50/50' : 'border-indigo-200 bg-indigo-50/50'}`}>
              <h3 className={`font-semibold text-sm mb-3 ${color === 'red' ? 'text-red-700' : 'text-indigo-700'}`}>
                {team.name}
                <span className="text-slate-400 font-normal ml-2">{stats.playerCount} players</span>
              </h3>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Avg CSR</div>
                  <div className={`text-lg font-bold ${color === 'red' ? 'text-red-700' : 'text-indigo-700'}`}>{stats.avgCSR || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">W / L</div>
                  <div className="text-sm font-semibold text-slate-700">
                    <span className="text-emerald-600">{stats.totalWins}</span>
                    <span className="text-slate-300 mx-0.5">/</span>
                    <span className="text-red-500">{stats.totalLosses}</span>
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Win %</div>
                  <div className={`text-sm font-semibold ${stats.winPct >= 50 ? 'text-emerald-600' : 'text-slate-500'}`}>{stats.winPct}%</div>
                </div>
                <div>
                  <div className="text-xs text-slate-400 mb-0.5">Matches</div>
                  <div className="text-sm font-semibold text-slate-700">{stats.totalMatches}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="flex-1 overflow-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center h-80">
              <LoadingSpinner size="lg" message="Loading team data..." />
            </div>
          ) : chartData.length === 0 ? (
            <div className="flex items-center justify-center h-80">
              <div className="text-center">
                <p className="text-slate-500 font-medium">No comparison data</p>
                <p className="text-slate-400 text-sm mt-1">No {gameType} history available for these teams.</p>
              </div>
            </div>
          ) : (
            <>
              {/* Player legend by team */}
              <div className="grid grid-cols-2 gap-4 mb-5">
                {[
                  { teamPlayers: allPlayers.filter(p => p.team === 'A'), label: teamA.name },
                  { teamPlayers: allPlayers.filter(p => p.team === 'B'), label: teamB.name },
                ].map(({ teamPlayers, label }) => (
                  <div key={label}>
                    <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">{label}</div>
                    <div className="flex flex-wrap gap-2">
                      {teamPlayers.map(player => {
                        const isHidden = hiddenPlayers.has(player.id);
                        return (
                          <div
                            key={player.id}
                            className={`flex items-center gap-1.5 text-xs cursor-pointer transition-opacity ${isHidden ? 'opacity-35' : ''}`}
                            onClick={() => togglePlayer(player.id)}
                          >
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: getColor(player) }} />
                            <span className="font-medium text-slate-600">{player.name}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Chart */}
              <div className="h-72 sm:h-96">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart
                    data={chartData}
                    margin={{ top: 10, right: 10, left: 16, bottom: 12 }}
                    onMouseLeave={() => setFocusedTooltipPlayerId(null)}
                  >
                    {yearBands.map((band) => (
                      <ReferenceArea
                        key={band.year}
                        x1={band.startDate}
                        x2={band.endDate}
                        fill="#f8fafc"
                        fillOpacity={0.9}
                        strokeOpacity={0}
                      />
                    ))}
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis
                      dataKey="date"
                      tick={axisTick}
                      tickLine={false}
                      axisLine={false}
                      height={yearSections.length > 1 ? 82 : 48}
                      interval="preserveStartEnd"
                    />
                    <YAxis
                      fontSize={11}
                      stroke="#94a3b8"
                      tickLine={false}
                      axisLine={false}
                      domain={['dataMin - 2', 'dataMax + 2']}
                      width={52}
                    />
                     <Tooltip content={tooltipContent} />
                     <Customized component={yearLabelRenderer} />
                     <Customized component={renderStartMarkers} />
                     {allPlayers
                      .filter(p => !hiddenPlayers.has(p.id))
                      .map(player => (
                        <Line
                          key={player.id}
                          type="monotone"
                          dataKey={player.id}
                          stroke={getColor(player)}
                          strokeWidth={2}
                          strokeDasharray={player.team === 'B' ? '5 3' : undefined}
                           dot={renderPlayerDot(player.id)}
                           activeDot={renderPlayerActiveDot(player.id)}
                          name={player.id}
                          connectNulls
                          isAnimationActive={false}
                        />
                      ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>

              {/* Bottom summary tiles by team */}
              <div className="mt-4 pt-4 border-t border-slate-100">
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { teamPlayers: allPlayers.filter(p => p.team === 'A'), label: teamA.name, border: 'border-red-200' },
                    { teamPlayers: allPlayers.filter(p => p.team === 'B'), label: teamB.name, border: 'border-indigo-200' },
                  ].map(({ teamPlayers, label, border }) => {
                    const allHidden = teamPlayers.every(p => hiddenPlayers.has(p.id));
                    const toggleTeam = () => {
                      setHiddenPlayers(prev => {
                        const next = new Set(prev);
                        if (allHidden) {
                          teamPlayers.forEach(p => next.delete(p.id));
                        } else {
                          teamPlayers.forEach(p => next.add(p.id));
                        }
                        return next;
                      });
                    };
                    return (
                    <div key={label} className={`rounded-lg border ${border} p-3`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{label}</div>
                        <button
                          onClick={toggleTeam}
                          className="text-[10px] font-medium text-slate-400 hover:text-slate-600 transition-colors px-1.5 py-0.5 rounded hover:bg-slate-100"
                        >
                          {allHidden ? 'Show all' : 'Hide all'}
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {teamPlayers.map(player => {
                                const latestEntry = [...chartData].reverse().find(d => d[player.id] != null);
                                const rawVal = latestEntry?.[player.id];
                                const val = typeof rawVal === 'number' ? rawVal : undefined;
                          const isHidden = hiddenPlayers.has(player.id);
                          return (
                            <div
                              key={player.id}
                              className={`flex items-center gap-2 p-1.5 rounded cursor-pointer select-none transition-all ${
                                isHidden ? 'opacity-40' : 'hover:bg-slate-50'
                              }`}
                              onClick={() => togglePlayer(player.id)}
                            >
                              <div
                                className="w-2 h-2 rounded-full flex-shrink-0"
                                style={{ backgroundColor: isHidden ? '#94a3b8' : getColor(player) }}
                              />
                              <div className="min-w-0">
                                <div className={`text-xs truncate ${isHidden ? 'text-slate-400' : 'text-slate-500'}`}>{player.name}</div>
                                <div className={`text-sm font-bold ${isHidden ? 'text-slate-400' : 'text-slate-800'}`}>
                                  {val != null ? val : '—'}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamCompareModal;
